/**
 * @boli/core - Escrow Service
 *
 * Platform-agnostic escrow logic for Boli Live buyer protection system.
 * This service is used by BOTH Web (Phase 1) and Mobile (Phase 2) with ZERO changes.
 *
 * Key Features:
 * - View escrow status for orders
 * - Confirm delivery (starts 7-day countdown)
 * - File disputes
 * - View pending payouts (for sellers)
 *
 * Flow:
 * 1. Buyer pays for order → funds held in escrow
 * 2. Order delivered → buyer confirms delivery
 * 3. 7-day countdown starts
 * 4. If no dispute, funds auto-release to seller
 * 5. If disputed, admin resolves (release or refund)
 */

import type {
  EscrowTransactionFull,
  EscrowStatusResponse,
  PendingEscrowResponse,
  ApiResponse,
} from '@boli/types';

// ============================================================================
// INTERFACES
// ============================================================================

export interface IEscrowService {
  /**
   * Get escrow status for an order
   */
  getEscrowStatus(orderId: string): Promise<EscrowStatusResponse | null>;

  /**
   * Confirm delivery (buyer only) - starts 7-day countdown
   */
  confirmDelivery(orderId: string): Promise<ApiResponse<EscrowTransactionFull>>;

  /**
   * File a dispute (buyer only)
   */
  fileDispute(orderId: string, reason: string): Promise<ApiResponse<EscrowTransactionFull>>;

  /**
   * Get pending escrows for seller
   */
  getSellerPendingEscrows(): Promise<PendingEscrowResponse>;

  /**
   * Calculate days until release
   */
  calculateDaysUntilRelease(releaseDate: string): number;
}

export interface EscrowServiceConfig {
  apiBaseUrl: string;
  escrowHoldDays: number;
}

// ============================================================================
// DEFAULT CONFIG
// ============================================================================

export const DEFAULT_ESCROW_CONFIG: EscrowServiceConfig = {
  apiBaseUrl: '',
  escrowHoldDays: 7, // 7 days after delivery confirmation
};

// ============================================================================
// ESCROW SERVICE IMPLEMENTATION
// ============================================================================

export class EscrowService implements IEscrowService {
  private config: EscrowServiceConfig;
  private fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>;
  private getAuthToken: () => Promise<string | null>;

  constructor(
    config: Partial<EscrowServiceConfig>,
    fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
    getAuthToken: () => Promise<string | null>
  ) {
    this.config = { ...DEFAULT_ESCROW_CONFIG, ...config };
    this.fetchAdapter = fetchAdapter;
    this.getAuthToken = getAuthToken;
  }

  /**
   * Get authorization headers
   */
  private async getHeaders(): Promise<HeadersInit> {
    const token = await this.getAuthToken();
    return {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  }

  /**
   * Get escrow status for an order
   */
  async getEscrowStatus(orderId: string): Promise<EscrowStatusResponse | null> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/escrow/${orderId}`,
        { headers: await this.getHeaders() }
      );

      if (response.status === 404) {
        return null;
      }

      if (!response.ok) {
        throw new Error('Failed to fetch escrow status');
      }

      const data = await response.json();

      // Calculate days until release if applicable
      if (data.escrow?.releaseDate) {
        data.daysUntilRelease = this.calculateDaysUntilRelease(data.escrow.releaseDate);
      }

      return data;
    } catch (error) {
      console.error('Failed to get escrow status:', error);
      return null;
    }
  }

  /**
   * Confirm delivery (buyer only) - starts 7-day countdown
   */
  async confirmDelivery(orderId: string): Promise<ApiResponse<EscrowTransactionFull>> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/escrow/${orderId}/confirm-delivery`,
        {
          method: 'POST',
          headers: await this.getHeaders(),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: data.code || 'CONFIRM_FAILED',
            message: data.message || 'Failed to confirm delivery',
          },
        };
      }

      return { success: true, data: data.escrow };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * File a dispute (buyer only)
   */
  async fileDispute(orderId: string, reason: string): Promise<ApiResponse<EscrowTransactionFull>> {
    try {
      if (!reason || reason.trim().length < 10) {
        return {
          success: false,
          error: {
            code: 'INVALID_REASON',
            message: 'Please provide a detailed reason for the dispute (at least 10 characters)',
          },
        };
      }

      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/escrow/${orderId}/dispute`,
        {
          method: 'POST',
          headers: await this.getHeaders(),
          body: JSON.stringify({ reason: reason.trim() }),
        }
      );

      const data = await response.json();

      if (!response.ok) {
        return {
          success: false,
          error: {
            code: data.code || 'DISPUTE_FAILED',
            message: data.message || 'Failed to file dispute',
          },
        };
      }

      return { success: true, data: data.escrow };
    } catch (error) {
      return {
        success: false,
        error: {
          code: 'NETWORK_ERROR',
          message: error instanceof Error ? error.message : 'Network error',
        },
      };
    }
  }

  /**
   * Get pending escrows for seller
   */
  async getSellerPendingEscrows(): Promise<PendingEscrowResponse> {
    try {
      const response = await this.fetchAdapter(
        `${this.config.apiBaseUrl}/api/seller/escrow/pending`,
        { headers: await this.getHeaders() }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch pending escrows');
      }

      return await response.json();
    } catch (error) {
      console.error('Failed to get pending escrows:', error);
      return { escrows: [], totalPending: 0 };
    }
  }

  /**
   * Calculate days until release
   */
  calculateDaysUntilRelease(releaseDate: string): number {
    const release = new Date(releaseDate);
    const now = new Date();
    const diffTime = release.getTime() - now.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return Math.max(0, diffDays);
  }

  /**
   * Format escrow status for display
   */
  formatEscrowStatus(status: string): string {
    switch (status) {
      case 'held':
        return 'In Escrow';
      case 'released':
        return 'Released';
      case 'refunded':
        return 'Refunded';
      case 'disputed':
        return 'Under Dispute';
      default:
        return status;
    }
  }

  /**
   * Get status color for UI
   */
  getStatusColor(status: string): 'blue' | 'green' | 'red' | 'yellow' {
    switch (status) {
      case 'held':
        return 'blue';
      case 'released':
        return 'green';
      case 'refunded':
        return 'red';
      case 'disputed':
        return 'yellow';
      default:
        return 'blue';
    }
  }
}

// ============================================================================
// ESCROW SERVICE FACTORY
// ============================================================================

let escrowServiceInstance: EscrowService | null = null;

export function createEscrowService(
  config: Partial<EscrowServiceConfig>,
  fetchAdapter: (url: string, options?: RequestInit) => Promise<Response>,
  getAuthToken: () => Promise<string | null>
): EscrowService {
  escrowServiceInstance = new EscrowService(config, fetchAdapter, getAuthToken);
  return escrowServiceInstance;
}

export function getEscrowService(): EscrowService {
  if (!escrowServiceInstance) {
    throw new Error('EscrowService not initialized. Call createEscrowService first.');
  }
  return escrowServiceInstance;
}
